import { LifeCycleEventType, MonitoredTv } from 'app/model/CORE';
import { ConfiguredAirspace, MonitoredMeasure } from 'app/model/SOL49';
import TimePeriodUtils from './TimePeriodUtils';
import TimeUtils from './TimeUtils';
import AppContext from 'app/common/context/AppContext';

export class SortUtils {
    static STRING_SORTING(a: string, b: string) {
        return a.localeCompare(b);
    }

    static MONITORED_TV_ALPHABETICAL_SORTING<T extends MonitoredTv>(a: T, b: T) {
        return SortUtils.STRING_SORTING(a.refLocation.id, b.refLocation.id);
    }

    static CONFIGURED_AIRSPACE_ALPHABETICAL_SORTING(a: ConfiguredAirspace, b: ConfiguredAirspace) {
        return SortUtils.STRING_SORTING(a.airspace.id, b.airspace.id);
    }

    static MONITORED_MEASURE_NEW_CREATION_SORTING<T extends MonitoredMeasure>(a: T, b: T): number {
        const aIsNewMeasure =
            a.measure.lastUpdate.userUpdateType === LifeCycleEventType.CREATION &&
            TimeUtils.diffInMinutes(AppContext.getCurrentTime(), a.measure.lastUpdate.eventTime) < 3;
        const bIsNewMeasure =
            b.measure.lastUpdate.userUpdateType === LifeCycleEventType.CREATION &&
            TimeUtils.diffInMinutes(AppContext.getCurrentTime(), b.measure.lastUpdate.eventTime) < 3;
        if (aIsNewMeasure && bIsNewMeasure) {
            return TimeUtils.diffInMs(b.measure.lastUpdate.eventTime, a.measure.lastUpdate.eventTime);
        } else if (aIsNewMeasure) {
            return -1;
        } else if (bIsNewMeasure) {
            return 1;
        }

        return 0;
    }

    static MONITORED_MEASURE_ALPHABETICAL_SORTING<T extends MonitoredMeasure>(a: T, b: T) {
        const newMeasuresSorting = SortUtils.MONITORED_MEASURE_NEW_CREATION_SORTING(a, b);
        return newMeasuresSorting !== 0
            ? newMeasuresSorting
            : a.measure.measureId.id.localeCompare(b.measure.measureId.id);
    }

    static MONITORED_MEASURE_DURATION_SORTING<T extends MonitoredMeasure>(a: T, b: T) {
        const newMeasuresSorting = SortUtils.MONITORED_MEASURE_NEW_CREATION_SORTING(a, b);
        return newMeasuresSorting !== 0
            ? newMeasuresSorting
            : TimeUtils.diffInMs(
                  TimePeriodUtils.getDurationInMs(a.measure.applicability),
                  TimePeriodUtils.getDurationInMs(b.measure.applicability),
              );
    }

    static MONITORED_MEASURE_START_TIME_SORTING<T extends MonitoredMeasure>(a: T, b: T) {
        const newMeasuresSorting = SortUtils.MONITORED_MEASURE_NEW_CREATION_SORTING(a, b);
        return newMeasuresSorting !== 0
            ? newMeasuresSorting
            : TimeUtils.diffInMs(a.measure.applicability.from, b.measure.applicability.from);
    }

    static MONITORED_MEASURE_END_TIME_SORTING<T extends MonitoredMeasure>(a: T, b: T) {
        const newMeasuresSorting = SortUtils.MONITORED_MEASURE_NEW_CREATION_SORTING(a, b);
        return newMeasuresSorting !== 0
            ? newMeasuresSorting
            : TimeUtils.diffInMs(a.measure.applicability.to, b.measure.applicability.to);
    }
}
